import { useCallback, useEffect, useRef } from "react";
import { useAsyncTask } from "src-next/shared/utils/useAsyncTask";
import { resolveDocumentBinaryDataUrl } from "../services/patientSupportingDocumentsService";

interface DocumentBinaryPreviewParams {
  dataUrl?: string;
  binaryUrl?: string;
  mimeType?: string;
}

const EMPTY_BINARY = { dataUrl: "", mimeType: "application/octet-stream" };

/**
 * Carrega o Binary do documento só quando o preview/download precisa.
 * Se `dataUrl` já existe (upload local), não faz request.
 */
export function useDocumentBinaryPreview(
  document: DocumentBinaryPreviewParams | null,
  enabled: boolean,
) {
  const existingDataUrl = document?.dataUrl?.trim() ?? "";
  const binaryUrl = document?.binaryUrl?.trim() ?? "";
  const fallbackMimeType =
    document?.mimeType?.trim() || "application/octet-stream";
  const resolvedKeyRef = useRef("");

  const task = useCallback(async () => {
    const result = await resolveDocumentBinaryDataUrl(
      binaryUrl,
      fallbackMimeType,
    );
    resolvedKeyRef.current = binaryUrl;
    return result;
  }, [binaryUrl, fallbackMimeType]);

  const { run, loading, data } = useAsyncTask({
    task,
    initialData: EMPTY_BINARY,
  });

  useEffect(() => {
    if (!enabled || existingDataUrl || !binaryUrl) return;
    void run();
  }, [enabled, existingDataUrl, binaryUrl, run]);

  if (existingDataUrl) {
    return {
      dataUrl: existingDataUrl,
      mimeType: fallbackMimeType,
      isLoading: false,
    };
  }

  const matchesCurrentDocument = resolvedKeyRef.current === binaryUrl;

  return {
    dataUrl: matchesCurrentDocument ? (data?.dataUrl ?? "") : "",
    mimeType: matchesCurrentDocument
      ? data?.mimeType || fallbackMimeType
      : fallbackMimeType,
    isLoading: Boolean(enabled && binaryUrl && (!matchesCurrentDocument || loading)),
  };
}
